import { motion } from "framer-motion";
import {
  TrendingDown,
  Puzzle,
  Factory,
  MessageSquare,
  Hourglass,
  CheckCircle2,
} from "lucide-react";

const problems = [
  {
    icon: TrendingDown,
    title: "Low Placements",
    desc: "Most graduates wait 6–12 months after their degree to land a first job.",
    accent: "border-t-aiblue",
    iconColor: "text-aiblue",
    iconBg: "bg-aiblue/10",
  },
  {
    icon: Puzzle,
    title: "Outdated Syllabus",
    desc: "Colleges still teach 2010-era textbooks in a 2026 AI economy.",
    accent: "border-t-aiorange",
    iconColor: "text-aiorange",
    iconBg: "bg-aiorange/10",
  },
  {
    icon: Factory,
    title: "Zero Industry Exposure",
    desc: "Students graduate having never worked on a real industry project.",
    accent: "border-t-aiblue",
    iconColor: "text-aiblue",
    iconBg: "bg-aiblue/10",
  },
  {
    icon: MessageSquare,
    title: "Soft Skills Gap",
    desc: "Technical knowledge without communication kills job prospects.",
    accent: "border-t-aiorange",
    iconColor: "text-aiorange",
    iconBg: "bg-aiorange/10",
  },
  {
    icon: Hourglass,
    title: "Career Support Too Late",
    desc: "Most colleges start placement prep only in the final semester.",
    accent: "border-t-aiblue",
    iconColor: "text-aiblue",
    iconBg: "bg-aiblue/10",
  },
];

// Solution-card collage (Unsplash): coding · hiring · student discussion · building projects
const SOLUTION_IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Student coding on a laptop",
  },
  {
    src: "/partners/smru/Handshake.png",
    alt: "Hiring — a professional handshake",
  },
  {
    src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Students discussing a project together",
  },
  {
    src: "https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Team building a real-world project",
  },
];

export default function SmruProblem() {
  return (
    <section id="why" className="pt-20 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-b from-[#FFF5F0] via-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiblue/10 text-aiblue">
            The Real Problem
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900 mt-6 leading-tight">
            Why do many engineering students{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              still struggle after graduation?
            </span>
          </h2>
          <p className="text-gray-600 text-lg mt-4">
           Four years of non-stop hustle, only to get ghosted by the job market?
Here is exactly where the promise breaks and how to fix this.</p>
        </motion.div>

        {/* Problem cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
          {problems.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                className={`bg-white rounded-2xl border border-gray-100 border-t-4 ${p.accent} shadow-sm p-6 hover:-translate-y-1 hover:shadow-xl transition`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center ${p.iconBg}`}
                >
                  <Icon size={40} className={p.iconColor} strokeWidth={1.75} />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mt-5">
                  {p.title}
                </h3>
                <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* The Solution — spread across the full card (navy treatment) */}
        <motion.div
          className="mt-16 relative overflow-hidden rounded-[2rem] shadow-xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          {/* navy background + glow accents (matching the quote band below) */}
          <div className="absolute inset-0 bg-[#0A2665]">
            <div className="absolute top-0 left-0 w-[320px] h-[320px] bg-blue-500/30 blur-[90px] rounded-full -translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 right-0 w-[320px] h-[320px] bg-orange-500/20 blur-[90px] rounded-full translate-x-1/4 translate-y-1/4" />
          </div>
          <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-white/20 rounded-tl-sm" />
          <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-white/20 rounded-br-sm" />

          <div className="relative p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12 text-white">
            {/* LEFT — content */}
            <div className="flex-1 flex items-start gap-5">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-amber-300" strokeWidth={2} />
              </div>
              <div>
                <span className="inline-block text-amber-300 text-xs font-bold uppercase tracking-[0.2em] mb-2">
                  The Solution
                </span>
                <h3 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-4">
                  The <span className="text-amber-300">AI.Prof × SMRU</span> Solution
                </h3>
                <div className="space-y-4">
                  <p className="text-white/95 text-base sm:text-lg leading-relaxed">
                    Together,{" "}
                    <span className="font-bold text-white">AI.Prof × SMRU</span> is
                    eliminating the career gamble to engineer the exact outcome you
                    deserve. From{" "}
                    <span className="font-bold text-white">Semester 1</span>, we arm
                    you with{" "}
                    <span className="font-bold text-white">real projects</span> and{" "}
                    <span className="font-bold text-white">cutting-edge AI tools</span>{" "}
                    to build undeniable proof of your talent.
                  </p>
                  <p className="text-white/90 text-base sm:text-lg leading-relaxed">
                    We close the gap between college and the real
                    world by matching your{" "}
                    <span className="font-bold text-white">verified portfolio</span>{" "}
                    directly with live company requirements making your exact
                    skills instantly visible to the right employers across our
                    network of{" "}
                    <span className="font-bold text-white">1,800+ hiring companies</span>{" "}
                    waiting for you.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT — Unsplash collage */}
            <div className="shrink-0 w-full lg:w-[22rem] grid grid-cols-2 gap-3">
              {SOLUTION_IMAGES.map((img) => (
                <img
                  key={img.src}
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="h-28 sm:h-32 w-full rounded-xl object-cover ring-1 ring-white/15 shadow-lg"
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Closing quote band */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 mx-auto max-w-3xl relative rounded-[32px] overflow-hidden shadow-xl"
        >
          <div className="absolute inset-0 bg-[#0A2665]">
            <div className="absolute top-0 left-0 w-[250px] h-[250px] bg-blue-500/30 blur-[80px] rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-orange-500/20 blur-[80px] rounded-full translate-x-1/4 translate-y-1/4" />
          </div>
          <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-white/20 rounded-tl-sm" />
          <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-white/20 rounded-br-sm" />
        </motion.div>
      </div>
    </section>
  );
}
